import { forwardRef, useState } from 'react';

import {
  Drawer,
  DrawerOverlayProvider,
  DrawerProps,
  Stack,
  SwipeableViews,
} from '@cast/design-system';

import { CreateEdgeLocationStep } from './CreateEdgeLocationStep';
import { EdgeLocationOnboardingStep } from './EdgeLocationOnboardingStep';
import { SelectAddonsStep } from './SelectAddonsStep';
import { EdgeLocationDrawerHeader } from '../../_components/EdgeLocationDrawerHeader';
import { EdgeLocationFormValues, EdgeLocationInitialData } from '../../types';

type StepSlide = 'create' | 'addons' | 'onboard';

export type CreateEdgeLocationDrawerProps = DrawerProps & {
  clusterId: string;
  edgeLocationId?: string;
  initialData?: EdgeLocationInitialData;
  hideSuccessNotification?: boolean;
};

export const CreateEdgeLocationDrawer = forwardRef<
  HTMLDivElement,
  CreateEdgeLocationDrawerProps
>(
  (
    {
      clusterId,
      edgeLocationId,
      initialData,
      hideSuccessNotification,
      ...props
    },
    ref
  ) => {
    const [createdEdgeLocationId, setCreatedEdgeLocationId] = useState<
      string | undefined
    >(edgeLocationId);
    const [pendingAddonsFormValues, setPendingAddonsFormValues] =
      useState<EdgeLocationFormValues | null>(null);
    const isReviewingScript = !!edgeLocationId;
    const [activeSlide, setActiveSlide] = useState<StepSlide>(
      isReviewingScript ? 'onboard' : 'create'
    );

    return (
      <Drawer
        ref={ref}
        testId="create-edge-location-drawer"
        size="medium"
        {...props}
      >
        <DrawerOverlayProvider>
          <Stack height="100%">
            <SwipeableViews
              sx={{
                flex: 1,
              }}
              slidesWrapperSx={{
                height: '100%',
              }}
              slideWrapperSx={{
                height: '100%',
              }}
              animateHeight={false}
              contentBefore={() => (
                <EdgeLocationDrawerHeader
                  isReviewingScript={isReviewingScript}
                />
              )}
              activeSlide={activeSlide}
              onSlideChange={(slide) => setActiveSlide(slide as StepSlide)}
              slides={{
                create: ({ isActive }) => (
                  <CreateEdgeLocationStep
                    initialData={initialData}
                    isActive={isActive}
                    onConfigureComplete={(formValues) => {
                      setPendingAddonsFormValues(formValues);
                    }}
                  />
                ),
                addons: () =>
                  pendingAddonsFormValues ? (
                    <SelectAddonsStep
                      key={`addons-${pendingAddonsFormValues.locationName}`}
                      clusterId={clusterId}
                      formValues={pendingAddonsFormValues}
                      hideSuccessNotification={hideSuccessNotification}
                      onCreateLocationSuccess={setCreatedEdgeLocationId}
                    />
                  ) : null,
                onboard: ({ isActive }) =>
                  isActive && (
                    <EdgeLocationOnboardingStep
                      clusterId={clusterId}
                      edgeLocationId={createdEdgeLocationId}
                      hideSuccessNotification={hideSuccessNotification}
                    />
                  ),
              }}
            />
          </Stack>
        </DrawerOverlayProvider>
      </Drawer>
    );
  }
);

CreateEdgeLocationDrawer.displayName = 'CreateEdgeLocationDrawer';
